import React, { useContext, useEffect, useRef, useState } from 'react';
import { TISRProject, TISRProjects, TransferModelOwnerPayload } from './DefineModel/model';
import { useHistory } from 'react-router-dom';
import { ChangeIcon, CopyIcon, DeleteIcon, OverFlowIcon } from 'assests/GmsIcons';
import { formatDate } from 'Common/HelperClass';
import TableCell from 'Components/Common/components/TableCell';
import { AppContext } from 'Context/AppContext';
import ChangeOwnerDialog, { User } from 'Pages/GMS/Rules/ChangeOwnerDialog';
import { copyModel, transferModelOwner } from './DefineModel/ModelServices/ModalServices';
import { toast } from 'react-toastify';
import OverflowOption from './OverflowOption';

type TISRProjectTableProps = {
    isrProjects: TISRProjects;
    setDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteItem: React.Dispatch<React.SetStateAction<{ id: string, name: string }>>;
    isGuide: boolean;
    updatePermission: boolean;
    fetchISRProjects: () => Promise<void>
}

const ISRProjectTable = ({ isrProjects, setDeleteModal, setDeleteItem, isGuide, updatePermission, fetchISRProjects }: TISRProjectTableProps) => {

    const [selectIndex, setSelectIndex] = useState<number | null>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [showOwnerTransferDialog, setShowOwnerTransferDialog] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<TISRProject | null>(null);
    const history = useHistory();
    const navigateToMainPage = (name: string, projectId: string = '') => {
        if (!updatePermission) {
            toast.warn('You do not have permission to edit this model');
            return;
        }
        if (isGuide) {
            history.push(`/gms/guide/main/${name}/${projectId}`);
        } else {
            history.push(`/gms/isr/main/${name}/${projectId}`);
        }
    }
    const appData = useContext(AppContext);
    const hasCreatePermission = () => (appData?.pm?.checkPermission("smartbuilderthree_create"))
    const hasOwnerTransferPermission = () => (appData?.pm?.checkPermission("smartbuilderthree_transfer"))
    const closeOwnerTransferDialog = () => setShowOwnerTransferDialog(false);

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom - window.scrollY,
            left: rect.left - window.scrollX,
        });
        setSelectIndex(index);
    };

    const ownerChangeSuccessMessage = (user: string) => `Model owner changed to ${user}. They can now edit and manage this model.`;
    const ownerChangeFailureMessage = "Model owner could not be changed";

    const onOwnerChange = async (data: TransferModelOwnerPayload) => {
        try {
            const response = await transferModelOwner(data)
            if (response?.data?.done) { toast.success(ownerChangeSuccessMessage(`${data.username}`)); }
            else { toast.error(ownerChangeFailureMessage) }
            closeOwnerTransferDialog()
        } catch (error) {
            console.log('Error', error);
            toast.error(ownerChangeFailureMessage)
        }
        finally {
            fetchISRProjects()
        }
    }

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setSelectIndex(null);
            }
        }
        function handleScroll() {
            if (selectIndex != null) {
                setSelectIndex(null);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [selectIndex]);

    const onClickedChangeOwner = (item: TISRProject) => {
        setShowOwnerTransferDialog(true);
        setSelectedItem(item);
    }

    const onClickedCopy = async (item: TISRProject) => {
        try {
            const response = await copyModel(item.id)
            if (response?.data?.done) { toast.success(response?.message || "Model copied successfully"); }
            else { toast.error(response?.message || "Model could not be copied"); }
        } catch (error) {
            console.log('Error', error);
            toast.error("Model could not be copied")
        }
        finally {
            fetchISRProjects()
        }
    }

    return (
        <div id="isr-project-table" className="tw-border tw-border-solid tw-mt-3 tw-border-[#DFE0E6] tw-border-r-2 ">
            <div id="table-scroll-container" className="table-container" style={{ width: '100%', height: 'auto', overflowX: 'auto' }}>
                <table id="isr-data-table" className="tw-bg-[#DFE1E6] tw-w-full tw-table-fixed">
                    <thead id="isr-table-header" className="tw-sticky tw-top-0 tw-bg-[#DFE1E6]">
                        <tr>
                            <th id="model-name-header" className="table-header-style" style={{ width: '20%' }}>Model Name</th>
                            <th id="environment-header" className="table-header-style" style={{ width: '20%' }} >Environment</th>
                            <th id="owner-header" className="table-header-style" style={{ width: '18%' }} >Owner</th>
                            <th id="updated-at-header" className="table-header-style" style={{ width: '12%' }}>Updated at</th>
                            <th id="submodels-header" className="table-header-style" >Submodels</th>
                            <th id="fields-header" className="table-header-style" >Fields</th>
                            <th id="categories-header" className="table-header-style" >Categories</th>
                            <th id="actions-header" className="table-header-style tw-w-[5%]"></th>
                        </tr>
                    </thead>
                    <tbody id="isr-table-body" className="tw-bg-white tw-divide-y tw-divide-[#F4F5F7]">
                        {isrProjects.map((row, rowIndex) => {
                            return <tr id={`isr-row-${rowIndex}`} key={rowIndex}>
                                <TableCell id={`model-name-${rowIndex}`} rowIndex={rowIndex} title={row?.ISRName ?? ''} onClick={() => navigateToMainPage(row?.ISRName ?? '', row?.id ?? '')}>{row?.ISRName ?? ''}</TableCell>
                                <TableCell id={`environment-${rowIndex}`} rowIndex={rowIndex}>{row?.channel ?? ''}</TableCell>
                                <TableCell id={`create-by-username-${rowIndex}`} rowIndex={rowIndex}>{row?.createdByUsername ?? '-'}</TableCell>
                                <TableCell id={`updated-at-${rowIndex}`} rowIndex={rowIndex}>{formatDate(Number(row?.updatedAt ?? row?.createdAt))}</TableCell>
                                <TableCell id={`submodels-${rowIndex}`} rowIndex={rowIndex}>{row?.numberOfDataModels ?? '0'}</TableCell>
                                <TableCell id={`fields-${rowIndex}`} rowIndex={rowIndex}>{row?.numberOfFields ?? '0'}</TableCell>
                                <TableCell id={`categories-${rowIndex}`} rowIndex={rowIndex}>{row?.numberOfCategories ?? '0'}</TableCell>
                                <TableCell id={`actions-${rowIndex}`} rowIndex={rowIndex}>
                                    <button id={`delete-btn-${rowIndex}`} onClick={(e) => handleOverflowClick(e, rowIndex)}>
                                        <OverFlowIcon />
                                    </button>
                                    {selectIndex === rowIndex && (
                                        <div ref={menuRef} className="tw-fixed tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0]" style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left + 40}px` }}>
                                            <div className={`tw-origin-top-left tw-mr-8 tw-right-0 tw-absolute tw-mx-3 tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0]`}>
                                                {hasCreatePermission() && OverflowOption(row, <CopyIcon />, "Copy", () => { setSelectIndex(null); onClickedCopy(row) }, true)}
                                                {hasOwnerTransferPermission() && OverflowOption(row, <ChangeIcon />, "Change Owner", () => { setSelectIndex(null); onClickedChangeOwner(row) }, true)}
                                                {OverflowOption(row, <DeleteIcon />, "Delete", () => {
                                                    setSelectIndex(null);
                                                    setDeleteItem({ id: row?.id, name: row?.ISRName })
                                                    setDeleteModal(true);
                                                }, true)}
                                            </div>
                                        </div>
                                    )}
                                </TableCell>
                            </tr>
                        })}
                        {showOwnerTransferDialog ? <ChangeOwnerDialog dialogHeader={"Change Owner"} labelName={"Select owner"} onClose={() => { closeOwnerTransferDialog(); setSelectedItem(null) }} onSubmit={(user: User) => { if (selectedItem != null) onOwnerChange({ model_id: selectedItem.id, userid: user.id, username: `${user?.first_name} ${user?.last_name}` }) }} /> : null}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ISRProjectTable;